import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Uppy from '@uppy/core'
import axios from 'axios'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

/**
 * Core file upload hook that manages Uppy instance creation, file processing,
 * and multipart upload handling.
 *
 * @returns {Object} Hook interface containing uppy instance and utility functions
 */
const useFileUpload = () => {
  const uppyRef = useRef(null)
  const [uppy, setUppy] = useState(null)
  const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL
  const userId = useSelector(state => state.user?.user?.username || null)

  /**
   * Modifies filename to be S3-compatible by replacing special characters
   * @param {string} currentFilename - Original filename
   * @returns {string} Modified filename with special characters replaced
   */
  const getModifiedName = useCallback(currentFilename => {
    let cf = currentFilename
      .replace(' ', '_')
      .replace('+', '_')
      .replace(',', '_')
    console.log('Current Filename: ', cf)
    return cf
  }, [])

  // onBeforeUpload function moved inline to createUppyInstance to prevent dependency issues

  /**
   * Creates multipart upload session with S3
   * @param {Object} file - File object from Uppy
   * @returns {Object} Upload session data with uploadId and key
   */
  const createMultipartUpload = useCallback(
    async file => {
      const url = `${API_GATEWAY_URL}/s3/multipart`
      const filename = getModifiedName(file.name)
      // Get current userId at upload time to avoid dependency issues
      const currentUserId = userId || localStorage.getItem('username')

      console.log('Before: ', url)
      console.log('File: ', file)
      console.log('filename: ', filename)

      const response = await axios.post(url, {
        filename: filename,
        type: file.type,
        metadata: { userId: currentUserId, name: filename, type: file.type }
      })
      console.log('Response: ', response)
      return { uploadId: response.data.uploadId, key: response.data.key }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [API_GATEWAY_URL, getModifiedName]
  )

  /**
   * Creates and configures Uppy instance
   */
  const createUppyInstance = useCallback(() => {
    const uppy = new Uppy({
      id: 'uppy-core',
      debug: true,
      autoProceed: false,
      restrictions: {
        maxFileSize: 1000000000,
        maxNumberOfFiles: 10000,
        minNumberOfFiles: null,
        allowedFileTypes: null
      },
      plugins: ['Dropbox'],
      onBeforeUpload: files => {
        // Inline onBeforeUpload to avoid dependency issues
        var updatedFiles = {}
        const currentUserId = userId || localStorage.getItem('username')

        for (const [key, file] of Object.entries(files)) {
          if (!file.meta.userId) {
            file.meta.userId = currentUserId
            updatedFiles[key] = {
              ...files[key],
              meta: {
                ...files[key].meta,
                userId: currentUserId
              }
            }
          }
        }
        return updatedFiles
      }
    })

    return uppy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Initialize Uppy instance on mount
   */
  useEffect(() => {
    if (!uppyRef.current) {
      uppyRef.current = createUppyInstance()
      setUppy(uppyRef.current) // Set state to trigger re-renders in dependent hooks
    }

    return () => {
      // Cleanup Uppy instance ONLY on unmount
      if (uppyRef.current) {
        uppyRef.current.close()
        uppyRef.current = null
        setUppy(null) // Clear state
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    uppy: uppy,
    getModifiedName,
    createMultipartUpload,
    API_GATEWAY_URL
  }
}

export default useFileUpload
